/**
 * createPortraitVideoTrack (Welle 6a überarbeitet)
 *
 * Erzeugt aus einer Berater-Webcam einen Portrait-Track für die Hochkant-Stele.
 *
 * - Source ist Portrait (z.B. Webcam mit Lagesensor, 1080x1920):
 *   Volles Bild wird übernommen, Canvas-Aspect = Source-Aspect, kürzere Seite 720
 * - Source ist Landscape (typische USB-Webcam, 1280x720):
 *   Center-Crop auf 9:16, Canvas 720x1280
 *
 * In beiden Fällen identisches Verhalten am Stele-Display: formatfüllend Hochkant.
 */

import { BackgroundCompositor, getBgConfig, getCamFlip, type BgConfig } from '@/lib/virtualBackground';

export interface PortraitPipeline {
  outputTrack: MediaStreamTrack;
  outputStream: MediaStream;
  stop: () => void;
  setEnabled: (on: boolean) => void;
  /** pre93: Output-Seitenverhältnis umschalten. Bei landscape-Gegenstelle (16:9) sendet der
   *  Berater sein VOLLES Bild (kein Hochkant-Crop), sonst Hochkant für die Portrait-Stele. */
  setLandscape: (on: boolean) => void;
  /** Welche Strategie aktiv ist – für UI/Logs */
  mode: 'portrait-passthrough' | 'landscape-centercrop';
}

export interface PortraitOpts {
  /** Kürzere Seite des Output-Canvas. Default 720. */
  shortSide?: number;
  /** Bei Landscape-Source: längere Seite des Outputs. Default 1280 (9:16 zu shortSide=720). */
  landscapeLongSide?: number;
  /** Output-FPS. Default 30. */
  fps?: number;
}

export function createPortraitVideoTrack(
  sourceStream: MediaStream,
  opts: PortraitOpts = {},
): PortraitPipeline | null {
  const sourceTrack = sourceStream.getVideoTracks()[0];
  if (!sourceTrack) {
    console.warn('[portraitTrack] kein Video-Track in source');
    return null;
  }

  const shortSide = opts.shortSide ?? 720;
  const landscapeLongSide = opts.landscapeLongSide ?? 1280;
  const fps = opts.fps ?? 30;

  const s = sourceTrack.getSettings();
  const sw0 = s.width ?? 0;
  const sh0 = s.height ?? 0;
  const isPortraitSource = sw0 > 0 && sh0 > 0 && sw0 < sh0;
  const mode: PortraitPipeline['mode'] =
    isPortraitSource ? 'portrait-passthrough' : 'landscape-centercrop';

  // Canvas-Größe bestimmen
  let canvasW: number, canvasH: number;
  if (isPortraitSource) {
    const sourceAspect = sw0 / sh0;
    canvasW = shortSide;
    canvasH = Math.round(shortSide / sourceAspect);
  } else {
    canvasW = shortSide;
    canvasH = landscapeLongSide;
  }
  const outputAspect_init = canvasW / canvasH;
  let outputAspect = outputAspect_init;
  // pre93: Standard-Hochkant-Maße merken; Landscape-Output ist 16:9 (landscapeLongSide × shortSide).
  const portraitW = canvasW;
  const portraitH = canvasH;
  const landW = landscapeLongSide;
  const landH = shortSide;
  let landscapeOut = false;

  const video = document.createElement('video');
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([sourceTrack]);
  video.style.position = 'absolute';
  video.style.left = '-10000px';
  video.style.width = '1px';
  video.style.height = '1px';
  document.body.appendChild(video);
  video.play().catch((e) => console.warn('[portraitTrack] play fail', e));

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    console.error('[portraitTrack] kein 2d-Context');
    try { video.remove(); } catch {}
    return null;
  }

  const outputStream = (canvas as any).captureStream(fps) as MediaStream;
  const outputTrack = outputStream.getVideoTracks()[0];
  if (!outputTrack) {
    console.error('[portraitTrack] captureStream lieferte keinen Track');
    return null;
  }

  let stopped = false;
  let enabled = true;
  let rafId: number | null = null;
  let lastDraw = 0;
  const targetInterval = 1000 / fps;

  // pre85b/c: Virtueller Hintergrund — Maske im Seg-Takt, Compositing im Zeichentakt (flüssig).
  let bgCfg: BgConfig = getBgConfig();
  let flip = getCamFlip();
  let compositor: BackgroundCompositor | null = null;
  let bgImg: HTMLImageElement | null = null;
  let bgImgSrc = '';
  const comp = document.createElement('canvas');
  const cctx = comp.getContext('2d')!;

  const loadBgImg = () => {
    if (bgCfg.mode === 'image' && bgCfg.imageSrc && bgCfg.imageSrc !== bgImgSrc) {
      bgImgSrc = bgCfg.imageSrc;
      const img = new Image();
      img.onload = () => { bgImg = img; };
      img.onerror = () => { bgImg = null; };
      img.src = bgCfg.imageSrc;
    }
  };
  const ensureCompositor = () => {
    if (bgCfg.mode !== 'none') {
      if (!compositor) { compositor = new BackgroundCompositor(); compositor.attach(video); }
      compositor.setActive(true);
      loadBgImg();
    } else if (compositor) {
      compositor.setActive(false);
    }
  };
  const onBgChange = (e: Event) => { bgCfg = (e as CustomEvent).detail as BgConfig; ensureCompositor(); };
  const onFlip = (e: Event) => { flip = !!(e as CustomEvent).detail; };
  window.addEventListener('bamoa:bg-changed', onBgChange);
  window.addEventListener('bamoa:cam-flip', onFlip);
  ensureCompositor();

  const draw = (ts: number) => {
    if (stopped) return;
    rafId = requestAnimationFrame(draw);
    if (ts - lastDraw < targetInterval - 1) return;
    lastDraw = ts;

    const sw = video.videoWidth;
    const sh = video.videoHeight;
    if (sw === 0 || sh === 0) return;

    if (!enabled) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // Quell-Region bestimmen: bei Portrait-Source = volles Bild, bei Landscape Center-Crop auf outputAspect
    let sx: number, sy: number, sWidth: number, sHeight: number;
    const sourceAspect = sw / sh;
    if (sourceAspect > outputAspect) {
      // Source breiter als Ziel → links/rechts beschneiden
      sHeight = sh;
      sWidth = sh * outputAspect;
      sx = (sw - sWidth) / 2;
      sy = 0;
    } else if (sourceAspect < outputAspect) {
      // Source schmaler als Ziel → oben/unten beschneiden (selten, eigentlich nur wenn Source noch portraitiger als Output)
      sWidth = sw;
      sHeight = sw / outputAspect;
      sx = 0;
      sy = (sh - sHeight) / 2;
    } else {
      sx = 0; sy = 0; sWidth = sw; sHeight = sh;
    }

    try {
      // pre85c: Person im Zeichentakt aus dem LIVE-Bild komponieren (flüssig);
      // Maske kommt im Seg-Takt. Fail-safe: ohne Maske normales Bild.
      let src: CanvasImageSource = video;
      const mask = bgCfg.mode !== 'none' && compositor ? compositor.getMask() : null;
      if (mask) {
        if (comp.width !== sw || comp.height !== sh) { comp.width = sw; comp.height = sh; }
        cctx.globalCompositeOperation = 'source-over';
        cctx.clearRect(0, 0, sw, sh);
        cctx.drawImage(mask, 0, 0, sw, sh);
        cctx.globalCompositeOperation = 'source-in';
        cctx.drawImage(video, 0, 0, sw, sh);
        cctx.globalCompositeOperation = 'destination-over';
        if (bgCfg.mode === 'blur') {
          cctx.filter = `blur(${bgCfg.blur ?? 12}px)`;
          cctx.drawImage(video, 0, 0, sw, sh);
          cctx.filter = 'none';
        } else if (bgCfg.mode === 'image' && bgImg) {
          const ir = bgImg.width / bgImg.height, cr = sw / sh;
          let dw = sw, dh = sh, dx = 0, dy = 0;
          if (ir > cr) { dh = sh; dw = sh * ir; dx = (sw - dw) / 2; } else { dw = sw; dh = sw / ir; dy = (sh - dh) / 2; }
          cctx.drawImage(bgImg, dx, dy, dw, dh);
        } else {
          cctx.drawImage(video, 0, 0, sw, sh);
        }
        cctx.globalCompositeOperation = 'source-over';
        src = comp;
      }
      if (flip) {
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(src, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(src, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
      }
    } catch {
      // ignore
    }
  };
  rafId = requestAnimationFrame(draw);

  console.log(
    `[portraitTrack] ${mode}: source ${sw0}x${sh0} → output ${canvasW}x${canvasH}@${fps}`,
  );

  return {
    outputTrack,
    outputStream,
    mode,
    stop() {
      stopped = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('bamoa:bg-changed', onBgChange);
      window.removeEventListener('bamoa:cam-flip', onFlip);
      try { compositor?.stop(); } catch { /* */ }
      compositor = null;
      try { outputStream.getTracks().forEach((t) => t.stop()); } catch {}
      try { video.srcObject = null; } catch {}
      try { video.remove(); } catch {}
    },
    setEnabled(on: boolean) {
      enabled = on;
    },
    setLandscape(on: boolean) {
      if (landscapeOut === on) return;
      landscapeOut = on;
      // Canvas-Maße umschalten — captureStream-Track folgt der Canvas-Auflösung,
      // WebRTC passt die gesendete Auflösung ohne Neuverhandlung an.
      canvas.width = on ? landW : portraitW;
      canvas.height = on ? landH : portraitH;
      outputAspect = canvas.width / canvas.height;
      console.log(`[portraitTrack] Output → ${on ? 'LANDSCAPE-Vollbild' : 'Hochkant'} ${canvas.width}x${canvas.height}`);
    },
  };
}
